import { chartTokens, SERIES_ORDER } from '@contract/chartTokens';

/**
 * REV-815: accessors for the shared chart colour table.
 *
 * The table itself is vendored from revenant-api in `@contract/chartTokens`
 * and is data only — it has to type-check here and run as CommonJS there, so
 * it cannot carry helpers. These are the TypeScript half; revenant-api has the
 * same lookup in `src/utils/chartTokens.js`. The values are shared, the lookup
 * is not, and that is the cheaper duplication by a wide margin.
 */

export type ThemeMode = 'light' | 'dark';

export type ChartToken = { fill: string; text: string };

export type TokenGroup = keyof typeof chartTokens;

/**
 * The categorical fills for a theme, in slot order — what Highcharts wants for
 * its `colors` option.
 *
 * Six slots, not more: past about six, colours that all clear 3:1 on the canvas
 * stop being reliably distinguishable from each other. A chart with a longer
 * tail should chart the top six and group the rest as one "Other" slice.
 */
export function seriesPalette(mode: ThemeMode = 'light'): string[] {
  // The cast is the price of the contract being plain data: SERIES_ORDER comes
  // across as readonly string[], not a literal union, because the upstream file
  // has to be valid CommonJS as well as valid TypeScript. The slots and the
  // table are declared next to each other in that file, and a mismatch would
  // fail the contract's own test upstream.
  const slots = SERIES_ORDER as readonly (keyof typeof chartTokens.series)[];
  return slots.map((slot) => chartTokens.series[slot][mode].fill);
}

/** group -> lowercased member name -> token, built once at module load. */
const LOOKUP = new Map<string, Map<string, { light: ChartToken; dark: ChartToken }>>(
  Object.entries(chartTokens).map(([group, members]) => [
    group,
    new Map(
      Object.entries(members as Record<string, { light: ChartToken; dark: ChartToken }>).map(
        ([name, token]) => [name.toLowerCase(), token]
      )
    ),
  ])
);

/**
 * Resolve one token.
 *
 * Case-insensitive, because callers pass values straight out of the database:
 * `severity` and the conformance vocabulary are stored lowercase, WCAG levels
 * uppercase. The token keys are the stored spellings verbatim —
 * `partially_supports`, not `partiallySupports` — and a parity test upstream
 * holds them to the models' own enums. Keying them on a prettier spelling is
 * what made seven of eight real values resolve to grey.
 *
 * An unknown name returns `neutral.unknown` rather than throwing: a finding
 * ingested from another tool can carry a severity this table has never heard
 * of, and a grey bar beats a thrown chart or a silently dropped slice. That is
 * for unknown EXTERNAL values only — one of our own vocabularies landing on
 * grey is a bug, not a graceful degradation.
 */
export function tokenFor(group: TokenGroup, name: string, mode: ThemeMode = 'light'): ChartToken {
  const index = LOOKUP.get(group);
  // `String(name)` rather than `String(name ?? '')`: the nullish guard was
  // dead. A JS caller passing null yields 'null', which matches no key and
  // falls through to neutral anyway — identical behaviour, verified — while
  // the `??` added a branch no TypeScript caller can reach, so it could only
  // ever sit uncovered.
  const entry = index?.get(String(name).toLowerCase());
  return entry ? entry[mode] : chartTokens.neutral.unknown[mode];
}
